import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search,
  Briefcase,
  Star,
  ExternalLink,
  Phone,
  Mail,
  Filter,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_FILTERS = [
  "All Domains",
  "Consulting",
  "Tech & Product",
  "Finance",
  "Marketing",
  "Sales",
  "Data Science",
  "HR",
  "Entrepreneurship",
];

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchMentors();
  }, [page, search, domainFilter]);

  const fetchMentors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/mentors`, {
        params: {
          skip: page * limit,
          limit,
          search,
          domain: domainFilter === "All Domains" ? "" : domainFilter,
        },
      });
      setMentors(response.data.mentors);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch mentors");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleDomainChange = (value) => {
    setDomainFilter(value);
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-manrope text-3xl font-bold text-gray-900">
          Mentor Network
        </h1>
        <p className="text-gray-500 mt-1">
          {total} mentors available for career guidance
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, company, or expertise..."
                value={search}
                onChange={handleSearch}
                className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#E8B923]"
                data-testid="mentor-search-input"
              />
            </div>
            <Select value={domainFilter} onValueChange={handleDomainChange}>
              <SelectTrigger
                className="w-full md:w-48 bg-white border-gray-200 text-gray-700"
                data-testid="domain-filter-select"
              >
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by domain" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {DOMAIN_FILTERS.map((domain) => (
                  <SelectItem key={domain} value={domain}>
                    {domain}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mentors Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : mentors.length === 0 ? (
        <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500">No mentors found</p>
            <p className="text-sm text-gray-400 mt-1">
              Upload mentor data to populate this list
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mentors.map((mentor) => (
            <Card
              key={mentor.id}
              className="bg-white border border-gray-200/80 hover:border-gray-300 transition-all duration-300"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
              data-testid={`mentor-card-${mentor.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {mentor.first_name} {mentor.last_name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {mentor.designation}
                    </p>
                  </div>
                  {mentor.rating > 0 && (
                    <div className="flex items-center gap-1 text-[#E8B923]">
                      <Star className="h-4 w-4 fill-current" />
                      <span className="text-sm font-medium">
                        {mentor.rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-gray-500 mb-3">
                  <Briefcase className="h-4 w-4" />
                  <span className="text-sm">{mentor.company || "Independent"}</span>
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {(mentor.functional_domain || []).slice(0, 2).map((domain, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="text-xs bg-[#E8B923]/10 text-[#B8860B]"
                    >
                      {domain}
                    </Badge>
                  ))}
                </div>

                <div className="flex flex-wrap gap-1 mb-4">
                  {(mentor.expertise || []).slice(0, 3).map((exp, i) => (
                    <Badge key={i} variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                      {exp}
                    </Badge>
                  ))}
                  {(mentor.expertise || []).length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-500">
                      +{mentor.expertise.length - 3} more
                    </Badge>
                  )}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                  <span>{mentor.completed_calls || 0} sessions completed</span>
                  {mentor.linkedin && (
                    <a
                      href={mentor.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-3 w-3" />
                      LinkedIn
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of{" "}
            {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              data-testid="mentor-prev-page-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              data-testid="mentor-next-page-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
